<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="$emit('close')">
    <div class="bg-white rounded-md shadow-lg p-5 w-[28rem]">
      <h3 class="text-base font-semibold mb-3">
        {{ mode === 'remove' ? 'Remove Tags' : 'Add Tags' }}
        ({{ recordIds.length }} record{{ recordIds.length === 1 ? '' : 's' }})
      </h3>
      <label class="block text-sm mb-1">Tags</label>
      <TagPicker v-model="selected" :placeholder="mode === 'remove' ? 'Pick tags to remove…' : 'Add tags…'" />
      <div class="text-xs text-gray-500 mt-2 mb-3">
        <template v-if="mode === 'remove'">
          Removes the chosen tags from each selected record. Other tags on those records are left alone.
        </template>
        <template v-else>
          Adds the chosen tags to each selected record. Existing tags on those records are kept.
        </template>
      </div>
      <div v-if="errorMsg" class="text-sm text-red-700 mb-2">{{ errorMsg }}</div>
      <div class="flex justify-end gap-2">
        <button
          class="text-sm px-3 py-1.5 border border-gray-400 rounded hover:bg-gray-100"
          @click="$emit('close')"
        >Cancel</button>
        <button
          class="text-sm px-3 py-1.5 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
          :disabled="applying || !selected.length"
          @click="apply"
        >{{ applying ? 'Saving…' : (mode === 'remove' ? 'Remove' : 'Add') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "tags-bulk-modal" };
</script>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import TagPicker from "./TagPicker.vue";
import { bulkAddTagsToExpenses, bulkRemoveTagsFromExpenses, tagErrorMessage } from "@/services/ExpenseTag.js";

const props = defineProps({
  recordIds: { type: Array, required: true },
  mode: { type: String, default: "add" }, // 'add' or 'remove'
});
const emit = defineEmits(["close", "applied"]);

const store = useStore();
const $toast = useToast();

const selected = ref([]);
const applying = ref(false);
const errorMsg = ref("");

async function apply() {
  if (!props.recordIds.length || !selected.value.length) return;
  applying.value = true;
  errorMsg.value = "";
  const token = store.state.auth.token;
  const names = selected.value.map((t) => t.name).filter(Boolean);
  const ids = selected.value.map((t) => t.id).filter((x) => x != null);
  try {
    let result;
    if (props.mode === 'remove') {
      result = await bulkRemoveTagsFromExpenses(props.recordIds, { tagNames: names, tagIds: ids }, token);
    } else {
      result = await bulkAddTagsToExpenses(props.recordIds, names, token);
    }
    const applied = (result.applied_ids || []).length;
    if (applied > 0) {
      $toast.open({
        message: `Tags ${props.mode === 'remove' ? 'removed from' : 'added to'} ${applied} record${applied === 1 ? '' : 's'}`,
        type: result.errors && result.errors.length ? 'warning' : 'success',
        duration: 3000,
      });
      emit("applied", result);
    } else {
      errorMsg.value = (result.errors && result.errors[0] && result.errors[0].detail) || 'No records updated';
    }
  } catch (error) {
    errorMsg.value = tagErrorMessage(error)
      || `Failed to ${props.mode === 'remove' ? 'remove' : 'add'} tags`;
  }
  applying.value = false;
}
</script>
